import type React from "react";
import type { GraphemeGlyph } from "@-/dev.improve.haniverse.lib.morpheme.grapheme.entities";
import type { GraphemeSvgGlyphStroke } from "@-/dev.improve.haniverse.routes.nav.app._components.morpheme.grapheme.glyph.svg.entities";
import { useMemo } from "react";
import { View } from "@-/com.npmjs.react-native";
import { useTheme } from "@-/dev.improve.haniverse.lib.theme";
import Svg, { G, Path } from "@-/com.npmjs.react-native-svg";
import { hasLength } from "@-/com.npmjs.ts-array-length";
import { GraphemeSvgGlyphFontFallback } from "#grapheme-svg-glyph-font-fallback";
import { GraphemeSvgGlyphStripGuide } from "#grapheme-svg-glyph-strip-guide";

export interface GraphemeSvgGlyphProps {
  /** Optional solid stroke color for a card front; other SVG glyphs stay monochrome. */
  color?: string;
  /** Rendered instead of strokes (no stroke data, or no codepoint). Defaults to GraphemeFontGlyph. */
  fallback?: React.ReactNode;
  glyph: GraphemeGlyph;
  showBackground?: boolean;
  size?: number;
  /**
   * Hanzi Writer outlines + medians in stroke order. `null` = the grapheme has NO stroke data: the
   * font glyph renders in the strokes' footprint (the deliberate no-strokes state).
   */
  strokeList: readonly GraphemeSvgGlyphStroke[] | null;
}

function getCodePoint(grapheme: string): number | null {
  return grapheme.codePointAt(0) ?? null;
}

export function GraphemeSvgGlyph({
  color,
  fallback,
  glyph,
  showBackground = true,
  size = 180,
  strokeList,
}: GraphemeSvgGlyphProps) {
  const colorScheme = useTheme();
  const isDarkMode = colorScheme === "dark";
  const codePoint = getCodePoint(glyph.original);

  const strokeFill = color ?? (isDarkMode ? "#FFFFFF" : "#000000");

  const guidePalette = useMemo(
    () =>
      isDarkMode
        ? {
            background: "#0B1325",
            frame: "#334155",
            grid: "#475569",
            gridOpacity: 0.65,
          }
        : {
            background: "#FBF6E8",
            frame: "#C7A768",
            grid: "#D9C08A",
            gridOpacity: 0.8,
          },
    [isDarkMode],
  );

  if (strokeList === null || !codePoint || hasLength(strokeList, 0)) {
    return (
      <GraphemeSvgGlyphFontFallback
        color={color}
        fallback={fallback}
        glyph={glyph}
        showBackground={showBackground}
        size={size}
      />
    );
  }

  return (
    <View
      className={
        showBackground ? "overflow-hidden rounded-xs border border-border bg-surface" : undefined
      }
      pointerEvents="none"
    >
      <View
        className="items-center justify-center"
        pointerEvents="none"
        style={{ height: size, width: size }}
      >
        <Svg height={size} viewBox="0 0 1024 1024" width={size}>
          {showBackground && (
            <GraphemeSvgGlyphStripGuide columnCount={1} guidePalette={guidePalette} />
          )}
          <G transform="scale(1,-1) translate(0,-900)">
            {strokeList.map((stroke, index) => (
              <Path d={stroke.path} fill={strokeFill} key={index} />
            ))}
          </G>
        </Svg>
      </View>
    </View>
  );
}
